import styles from '@/styles/Cart.module.scss'
import BaseLayout from '@/layout/BaseLayout/BaseLayout'
import Head from 'next/head'
import React, { useEffect } from 'react'
import { GetStaticProps } from 'next'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import { useTranslation } from 'next-i18next'
import { useDeleteCartMutation, useEditCartRowMutation, useGetCartQuery, usePrepareExpressCheckoutMutation } from '@/api/cart'
import CartStepBar from '@/components/cart/CartStepBar/CartStepBar'
import CartItem from '@/components/cart/CartItem/CartItem'
import CartSummary from '@/components/cart/CartSummary/CartSummary'
import CartShipping from '@/components/cart/CartShipping/CartShipping'
import Button from '@/components/buttons/Button/Button'
import classNames from 'classnames'
import LoadingOverlay from '@/components/common/LoadingOverlay/LoadingOverlay'
import { useRouter } from 'next/router'
import { useGetCustomerQuery } from '@/api/customer'
import { useDeviceType, DeviceType } from '@/utils/hooks/useDeviceType'
import CartMobileItem from '@/components/cart/CartMobileItem/CartMobileItem'
import CartMobileSummary from '@/components/cart/CartMobileSummary/CartMobileSummary'
import ButtonLink from '@/components/buttons/ButtonLink/ButtonLink'
import TrashIcon from '@/assets/svg/trash.svg'
import ShareIcon from '@/assets/svg/share.svg'
import useAnalyticsManager from '@/utils/hooks/useAnalyticsManager'
import useTriggerEffect from '@/utils/hooks/useTriggerEffect'
import { useCountry } from '@/utils/hooks/useCountry'
import ShareShoppingCart from '@/components/cart/ShareShoppingCart/ShareShoppingCart'
import { ParsedUrlQuery } from 'querystring'
import AddShoppingListToCart from '@/components/cart/AddShoppingListToCart/AddShoppingListToCart'
import { getPageTitle } from '@/utils/helpers'

type Props = {}

interface PageQueryParams extends ParsedUrlQuery {
  shoppinglist?: string;
}

const Cart = (props: Props) => {
  const { t } = useTranslation(['cart', 'login'])
  const { countryCode, country } = useCountry()
  const { data, isFetching } = useGetCartQuery(countryCode)
  const { data: customer } = useGetCustomerQuery()
  const [removeCart] = useDeleteCartMutation()
  const [createExpressCheckout, { isLoading }] = usePrepareExpressCheckoutMutation()
  const [showShareCartModal, setShowShareCartModal] = React.useState<boolean>(false)
  const [showAddSharedCartModal, setShowAddSharedCartModal] = React.useState<boolean>(false)
  const router = useRouter()
  const device = useDeviceType()
  const { viewCart } = useAnalyticsManager()

  const {
    shoppinglist,
  } = router.query as PageQueryParams

  const onClickLogin = () => router.push('/login')

  const onClickRegister = () => router.push('/register')

  const prepareExpressCheckout = async () => {
    const checkoutData = await createExpressCheckout({
      countryCode: countryCode,
    })

    if ('data' in checkoutData && checkoutData.data.orderId) {
      window.location.href = `/cart/payment/${checkoutData.data.orderId}`
    }
  }

  const onClickEmptyCart = () => {
    removeCart()
  }

  const onCloseSharedCart = () => {
    setShowAddSharedCartModal(false)
    router.push(router.pathname, undefined, { shallow: true })
  }

  useTriggerEffect(() => {
    if (data)
      viewCart(data)
  }, [data, viewCart], data !== undefined && !isFetching)

  useEffect(() => {
    if (shoppinglist) {
      setShowAddSharedCartModal(true)
    }
  }, [shoppinglist])

  const isLoggedIn = customer?.id

  const showLoading = isFetching || isLoading

  return (
    <BaseLayout>
      <Head>
        <title>{getPageTitle(t('title'))}</title>
      </Head>
      <main className={styles.Cart}>
        <CartStepBar
          cartActive={true}
          shippingActive={false}
          showShipping={!data?.expressCheckout}
          paymentActive={false}
          confirmationActive={false}
          onClick={() => { }}
        />
        {showLoading && (
          <LoadingOverlay
            background
          />
        )}
        <div className={styles.content}>
          <h3>{t('viewHeader')}</h3>
          <div className={styles.cartItems}>
            {data?.rows?.map((item) => (
              device === DeviceType.Desktop ? (
                <CartItem
                  key={item.id}
                  item={item}
                />
              ) : (
                <CartMobileItem
                  key={item.id}
                  item={item}
                />
              )
            ))}
          </div>
          {device === DeviceType.Desktop && (
            <>
              <CartShipping
                cart={data}
              />
              <CartSummary
                cart={data}
              />
            </>
          )}
          {device === DeviceType.Mobile && (
            <CartMobileSummary
              cart={data}
            />
          )}
          {data?.rows && data.rows.length > 0 && (
            <div className={styles.linkButtons}>
              <ButtonLink
                label={t('cart:emptyCart')}
                icon={TrashIcon}
                onClick={onClickEmptyCart}
              />
              <ButtonLink
                label={t('cart:shareShoppingCart')}
                icon={ShareIcon}
                onClick={() => setShowShareCartModal(true)}
              />
            </div>
          )}
          <div className={classNames(styles.cartfooter)}>
            {!isLoggedIn && (
              <>
                <div className={classNames(styles.newCustomerInfo)}>
                  <h4>{t('login:newCustomer')}</h4>
                  <p>
                    {t('login:newCustomerInfo')}
                  </p>
                  <Button
                    label={t('cart:register')}
                    rounded={true}
                    medium={true}
                    onClick={onClickRegister}
                  />
                  <h4>{t('login:alreadyCustomer')}</h4>
                  <p>
                    {t('login:alreadyCustomerInfo')}
                  </p>
                  <Button
                    label={t('cart:login')}
                    lightDark={true}
                    medium={true}
                    rounded={true}
                    onClick={onClickLogin}
                  />
                </div>
                {data?.expressCheckout && (
                  <div>
                    <div className={classNames(styles.buttons)}>
                      <Button
                        label={t('cart:payment')}
                        rounded={true}
                        medium={true}
                        onClick={prepareExpressCheckout}
                      />
                    </div>
                    {country?.isExportEu && (
                      <p className='flex right margin-top-1'>
                        {t('cart:exportEuropeInfo')}
                      </p>
                    )}
                  </div>
                )}
              </>
            )}
            {isLoggedIn && data?.rows && data.rows.length > 0 && (
              <>
                <div></div>
                <div className={classNames(styles.buttons)}>
                  <Button
                    label={t('cart:delivery')}
                    rounded={true}
                    large={device === DeviceType.Mobile}
                    medium={true}
                    onClick={() => router.push('/cart/delivery')}
                  />
                </div>
              </>
            )}
          </div>
        </div>
        {showShareCartModal && (
          <ShareShoppingCart
            onClose={() => setShowShareCartModal(false)}
          />
        )}
        {showAddSharedCartModal && shoppinglist && (
          <AddShoppingListToCart
            onClose={onCloseSharedCart}
            shoppingListId={shoppinglist}
          />
        )}
      </main>
    </BaseLayout>
  )
}

export default Cart

export const getStaticProps: GetStaticProps<Props> = async ({
  locale,
}) => ({
  props: {
    ...(await serverSideTranslations(locale ?? 'en', [
      'common',
      'cart',
      'login',
      'footer',
    ])),
  },
})